import { CartItems } from '@/hooks/CartContext';
import { HandlePrices } from '@/shared/calculatePrices';
import {
    ClientProps,
    PercentualFornecedorProps,
    PriceProductProps,
    ProductProps,
    TaxSubstitutionProps,
} from '@/types';
import {
    applyComboPriceBenefitToProduct,
    ComboCartItemAllocation,
    getComboDiscountParticipatingQuantityForCartItem,
    getComboPriceBenefitForCartItem,
} from './comboCartUtils';

export interface ComboCartLinePriceDeps {
    combos: Array<{ id_campanha?: string; regras_beneficio?: unknown }>;
    isComboCompleteById: (idCampanha: string) => boolean;
    condicoes: number[];
    handlePrices: (
        condicoes: number[],
        product: ProductProps,
        parametroPrecoBase: unknown,
        percentualFornecedor: PercentualFornecedorProps[],
        listSubstTrib: TaxSubstitutionProps[],
        precoPromocao: unknown,
        from: string,
        options?: { skipPaymentAccrual?: boolean }
    ) => PriceProductProps[];
    findPrice: (
        prices: PriceProductProps[],
        option_uid: string,
        paymentCondition: number
    ) => PriceProductProps;
    priceCalcParams: {
        parametroPreco: unknown;
        percentualFornecedor: PercentualFornecedorProps[];
        listSubstTrib: TaxSubstitutionProps[];
        parametroPrecoPromocao: unknown;
    };
}

const getUnitPrice = (price?: PriceProductProps | null) =>
    price?.VLR_FINAL ?? price?.VLR_PRECO ?? null;

export const roundMoney = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    return Number(value.toFixed(2));
};

const isValidPriceRecord = (price?: PriceProductProps | null): price is PriceProductProps =>
    getUnitPrice(price) != null || price?.VLR_PRECO_CALCULADO != null || price?.ST_VLR_FINAL != null;

const toPriceList = (rawPrecos: ProductProps['PRECO']): PriceProductProps[] => {
    if (!rawPrecos) return [];
    return Array.isArray(rawPrecos) ? rawPrecos : Object.values(rawPrecos);
};

export const getComboUnitPriceForCartLine = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid'>,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps
): number | null => {
    const { combos, isComboCompleteById, condicoes, handlePrices, findPrice, priceCalcParams } = deps;

    if (!condicoes.length || !allocations?.length || !baseProduct?.COD_PRODUTO) return null;

    const comboBenefit = getComboPriceBenefitForCartItem(
        allocations,
        combos,
        isComboCompleteById,
        { sku: item.sku, option_uid: item.option_uid }
    );

    if (!comboBenefit) return null;

    if (comboBenefit.type === 'PRECO_FIXO') {
        return comboBenefit.fixedPrice;
    }

    const benefitProduct = applyComboPriceBenefitToProduct(baseProduct, comboBenefit, item.option_uid);

    const precos = handlePrices(
        condicoes,
        benefitProduct,
        priceCalcParams.parametroPreco,
        priceCalcParams.percentualFornecedor,
        priceCalcParams.listSubstTrib,
        priceCalcParams.parametroPrecoPromocao,
        'comboCartTotals',
        undefined
    );

    return getUnitPrice(findPrice(precos, item.option_uid, paymentCondition));
};

export const computeCartLineTotalWithComboDiscount = (
    item: CartItems,
    normalUnitPrice: number,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps
): number => {
    const discountQty = getComboDiscountParticipatingQuantityForCartItem(item, allocations);
    if (discountQty <= 0) {
        return roundMoney(normalUnitPrice * item.quantity);
    }

    const comboUnitPrice = getComboUnitPriceForCartLine(
        item,
        allocations,
        baseProduct,
        paymentCondition,
        deps
    );

    if (comboUnitPrice == null) {
        return roundMoney(normalUnitPrice * item.quantity);
    }

    const regularQty = Math.max(item.quantity - discountQty, 0);
    return roundMoney(comboUnitPrice * discountQty + normalUnitPrice * regularQty);
};

export const getNormalPriceRecordForCartLine = (
    item: Pick<CartItems, 'sku' | 'option_uid'>,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps
): PriceProductProps | null => {
    const { condicoes, handlePrices, findPrice, priceCalcParams } = deps;

    const precosFromProduct = toPriceList(baseProduct.PRECO);
    if (precosFromProduct.length) {
        const fromCatalog = findPrice(precosFromProduct, item.option_uid, paymentCondition);
        if (isValidPriceRecord(fromCatalog)) return fromCatalog;
    }

    if (!condicoes.length || !baseProduct?.COD_PRODUTO) return null;

    const precos = handlePrices(
        condicoes,
        baseProduct,
        priceCalcParams.parametroPreco,
        priceCalcParams.percentualFornecedor,
        priceCalcParams.listSubstTrib,
        priceCalcParams.parametroPrecoPromocao,
        'comboCartTotals-normal'
    );

    const found = findPrice(precos, item.option_uid, paymentCondition);
    return isValidPriceRecord(found) ? found : null;
};

export const getComboPriceRecordForCartLine = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid'>,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps
): PriceProductProps | null => {
    const { combos, isComboCompleteById, condicoes, handlePrices, findPrice, priceCalcParams } = deps;

    if (!condicoes.length || !allocations?.length || !baseProduct?.COD_PRODUTO) return null;

    const comboBenefit = getComboPriceBenefitForCartItem(
        allocations,
        combos,
        isComboCompleteById,
        { sku: item.sku, option_uid: item.option_uid }
    );

    if (!comboBenefit) return null;

    const benefitProduct = applyComboPriceBenefitToProduct(baseProduct, comboBenefit, item.option_uid);
    const calcOptions =
        comboBenefit.type === 'PRECO_FIXO' ? { skipPaymentAccrual: true as const } : undefined;

    const precos = handlePrices(
        condicoes,
        benefitProduct,
        priceCalcParams.parametroPreco,
        priceCalcParams.percentualFornecedor,
        priceCalcParams.listSubstTrib,
        priceCalcParams.parametroPrecoPromocao,
        'comboCartTotals-combo',
        calcOptions
    );

    const found = findPrice(precos, item.option_uid, paymentCondition);
    if (comboBenefit.type === 'PRECO_FIXO' && isValidPriceRecord(found)) {
        return {
            ...found,
            VLR_PRECO: comboBenefit.fixedPrice,
            VLR_PRECO_CALCULADO: comboBenefit.fixedPrice,
            VLR_FINAL: comboBenefit.fixedPrice + (found.ST_VLR_FINAL ?? 0),
        };
    }

    return isValidPriceRecord(found) ? found : null;
};

export interface CheckoutLineStPricing {
    stLineTotal: number;
    stPerPackaging: number;
}

/** Mesma regra do componente StPrice (fonte de verdade do carrinho). */
export const computeStLineTotalLikeStPrice = (
    priceRecord: PriceProductProps | null | undefined,
    quantity: number,
    client: ClientProps | null | undefined,
    listaTributacao: TaxSubstitutionProps[] | undefined,
    percentualFlex = 0
): number => {
    if (!priceRecord || quantity <= 0) return 0;

    const precoSt = priceRecord.ST_VLR_FINAL ?? 0;
    const precoCalculado = priceRecord.VLR_PRECO_CALCULADO;
    const precoPauta = priceRecord.VLR_PAUTA ?? 0;
    const codTrib = priceRecord.COD_TRIBUTACAO_PRODUTO;
    const tribList = listaTributacao ?? [];

    if (
        typeof percentualFlex === 'number' &&
        percentualFlex !== 0 &&
        client &&
        codTrib &&
        precoCalculado &&
        tribList.length
    ) {
        const precoComFlex = precoCalculado + (precoCalculado / 100) * percentualFlex;
        const tributacao = HandlePrices.getTributacao(client, codTrib, tribList);
        const calculado = HandlePrices.CalculaST(tributacao, precoComFlex * quantity, precoPauta);
        return calculado || precoSt || 0;
    }

    if (!quantity || quantity <= 1) {
        return precoSt || 0;
    }

    if (client && codTrib && precoCalculado && tribList.length) {
        const tributacao = HandlePrices.getTributacao(client, codTrib, tribList);
        const calculado = HandlePrices.CalculaST(tributacao, precoCalculado * quantity, precoPauta);
        return calculado || precoSt || 0;
    }

    return precoSt || 0;
};

/** ST da linha no checkout, alinhado ao carrinho (inclui desconto de combo). */
export const computeCheckoutLineStPricing = (
    item: CartItems,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps,
    clientForTax: ClientProps,
    listaTributacao: TaxSubstitutionProps[],
    percentualFlex = 0
): CheckoutLineStPricing => {
    const quantity = item.quantity || 0;
    if (quantity <= 0) {
        return { stLineTotal: 0, stPerPackaging: 0 };
    }

    const normalRecord = getNormalPriceRecordForCartLine(item, baseProduct, paymentCondition, deps);
    if (!normalRecord) {
        return { stLineTotal: 0, stPerPackaging: 0 };
    }

    const discountQty = getComboDiscountParticipatingQuantityForCartItem(item, allocations);
    const comboRecord =
        discountQty > 0
            ? getComboPriceRecordForCartLine(item, allocations, baseProduct, paymentCondition, deps)
            : null;
    const regularQty = Math.max(quantity - discountQty, 0);

    let stLineTotal = 0;

    if (discountQty > 0 && comboRecord) {
        stLineTotal += computeStLineTotalLikeStPrice(
            comboRecord,
            discountQty,
            clientForTax,
            listaTributacao,
            percentualFlex
        );
    }

    if (regularQty > 0) {
        stLineTotal += computeStLineTotalLikeStPrice(
            normalRecord,
            regularQty,
            clientForTax,
            listaTributacao,
            percentualFlex
        );
    }

    const primaryRecord = discountQty > 0 && comboRecord ? comboRecord : normalRecord;
    const stPerPackaging = computeStLineTotalLikeStPrice(
        primaryRecord,
        1,
        clientForTax,
        listaTributacao,
        percentualFlex
    );

    return {
        stLineTotal: Number(stLineTotal.toFixed(2)),
        stPerPackaging: Number(stPerPackaging.toFixed(2)),
    };
};

/** Fallback de ST a partir do registro de preço (mesma regra do componente StPrice). */
export const resolveStFromPriceRecord = (
    priceRecord: PriceProductProps | null | undefined,
    quantity: number,
    client: ClientProps,
    listaTributacao: TaxSubstitutionProps[],
    percentualFlex = 0
): CheckoutLineStPricing => {
    const stLineTotal = computeStLineTotalLikeStPrice(
        priceRecord,
        quantity,
        client,
        listaTributacao,
        percentualFlex
    );
    const stPerPackaging = computeStLineTotalLikeStPrice(
        priceRecord,
        1,
        client,
        listaTributacao,
        percentualFlex
    );

    return {
        stLineTotal: Number(stLineTotal.toFixed(2)),
        stPerPackaging: Number(stPerPackaging.toFixed(2)),
    };
};
